import {toInt} from '@/library/math';
import {POLL_RANGE_NO_EXPIRED, POLL_TYPE_EVENT} from '@/library/modules/poll';
import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';
import {addCalendarRoutes} from './calendar';

const CategoryManagement = () => import('../components/CategoryManagement.vue');

const EventList        = () => import('../views/event/EventList.vue');
const EventEntryEdit   = () => import('../views/event/entry/EventEntryEdit.vue');
const EventEntryView   = () => import('../views/event/entry/EventEntryView.vue');
const EventPrint       = () => import('../views/event/EventPrint.vue');
const EventCoursePrint = () => import('../views/event/EventCoursePrint.vue');
const EventCourseEdit  = () => import('../views/event/course/EventCourseEdit.vue');
const EventCourseView  = () => import('../views/event/course/EventCourseView.vue');

const EventCourseBookingList    = () => import('../views/event/course/export/EventCourseBookingList.vue');
const EventCourseBookingView    = () => import('../views/event/course/export/EventCourseBookingView.vue');
const EventCourseBookingClient  = () => import('../views/event/course/export/EventCourseBookingClient.vue');
const EventCourseBookingConfirm = () => import('../views/event/course/export/EventCourseBookingConfirm.vue');
const EventCourseBookingCancel  = () => import('../views/event/course/export/EventCourseBookingCancel.vue');

const EventSettings                    = () => import('../views/event/EventSettings.vue');
const EventEditors                     = () => import('../views/event/settings/EventEditors.vue');
const EventCourseSettingsWebExport     = () => import('../views/event/settings/EventCourseSettingsWebExport.vue');
const EventCourseSettingsPrivacy       = () => import('../views/event/settings/EventCourseSettingsPrivacy.vue');
const EventCourseSettingsLegalNotice   = () => import('../views/event/settings/EventCourseSettingsLegalNotice.vue');
const EventCourseSettingsEmail         = () => import('../views/event/settings/EventCourseSettingsEmail.vue');
const EventCourseSettingsEmailTemplate = () => import('../views/event/settings/mailTemplates/EventCourseSettingsEmailTemplate.vue');
const EventCourseMailTemplateEdit      = () => import('../views/event/settings/mailTemplates/EventCourseMailTemplateEdit.vue');
const EventSettingsGlobal              = () => import('../views/event/settings/EventSettingsGlobal.vue');
const EventCourseSettingsSite          = () => import('../views/event/settings/EventCourseSettingsSite.vue');
const EventCourseSettingsContact       = () => import('../views/event/settings/EventCourseSettingsContact.vue');
const EventCourseSettingsContactForm   = () => import('../views/event/settings/EventCourseSettingsContactForm.vue');
const EventCourseSettingsReminder      = () => import('../views/event/settings/EventCourseSettingsReminder.vue');

const EventCourseAssistantOutro = () => import('../views/event/assistant_course/EventCourseAssistantOutro.vue');
const EventCourseAssistant      = () => import('../views/event/assistant_course/EventCourseAssistant.vue');

const PollList  = () => import('../views/poll/PollList.vue');
const PollView  = () => import('../views/poll/PollView.vue');
const PollEdit  = () => import('../views/poll/PollEdit.vue');
const PollPrint = () => import('../views/poll/PollPrint.vue');

const Booking             = () => import('../views/event/course/booking/EventBooking.vue');
const EventBookingList    = () => import('../views/event/course/booking/components/EventBookingList.vue');
const EventBookingViewTab = () => import('../views/event/course/booking/components/EventBookingViewTab.vue');
const EventBookingClient  = () => import('../views/event/course/booking/components/EventBookingClient.vue');
const EventBookingConfirm = () => import('../views/event/course/booking/components/EventBookingConfirm.vue');
const EventBookingCancel  = () => import('../views/event/course/booking/components/EventBookingCancel.vue');

const resource       = 'event';
const meta: MetaData = {
    module: 'event',
    assistant: {
        key: 'event_course',
        onComplete: {name: 'event-list'},
        steps: [
            {
                title: 'Veranstaltung',
                titleIcon: 'support',
                component: EventCourseAssistant,
            },
            {
                component: EventCourseAssistantOutro,
            },
        ],
    },
};

const metaBooking: MetaData = {
    module: resource,
    layout: 'blank',
    allowLoggedOut: true,
    disableNotifications: true,
    disableScrollOnParamChange: true,
};

let routes: RouteRecordRaw[] = [
    {
        name: 'event-list-mine',
        path: '/event/list/mine',
        component: EventList,
        props()
        {
            return {
                resource: 'event',
                type: 'tile',
                mine: 1,
            };
        },
        meta,
    },
    {
        path: '/event/entry/print/:id/:start',
        name: 'event-entry-print',
        component: EventPrint,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: toInt(route.params.id),
                start: route.params.start,
            };
        },
    },
    {
        path: '/event/course/print/:id',
        name: 'event-course-print',
        component: EventCoursePrint,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: toInt(route.params.id),
            };
        },
    },
    {
        name: 'event-poll-view',
        path: '/event/poll/view/:id',
        component: PollView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: toInt(route.params.id),
                type: POLL_TYPE_EVENT,
            };
        },
    },
    {
        name: 'event-poll-print',
        path: '/event/poll/print/:id',
        component: PollPrint,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: toInt(route.params.id),
                type: POLL_TYPE_EVENT,
            };
        },
    },
    {
        name: 'event-poll-add',
        path: '/event/poll/add',
        component: PollEdit,
        meta,
        props()
        {
            return {
                type: POLL_TYPE_EVENT,
            };
        },
    },
    {
        name: 'event-poll-edit',
        path: '/event/poll/edit/:id',
        component: PollEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: toInt(route.params.id),
                type: POLL_TYPE_EVENT,
            };
        },
    },
    {
        name: 'event-poll-list',
        path: '/event/poll/list/:range?',
        component: PollList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                range: 'undefined' !== typeof route.params.range ? toInt(route.params.range) : POLL_RANGE_NO_EXPIRED,
                type: POLL_TYPE_EVENT,
            };
        },
    },
    {
        path: '/event/settings/:route(intranet)?',
        name: 'event-settings',
        component: EventSettings,
        meta,
        alias: [
            '/event/settings/:route(intranet)?/:wildcard*',
        ],
        children: [
            {
                path: 'reminder',
                name: 'event-settings-course-reminder',
                component: EventCourseSettingsReminder,
                meta,
            },
            {
                path: 'site',
                name: 'event-settings-course-site',
                component: EventCourseSettingsSite,
                meta,
            },
            {
                path: 'webexport',
                name: 'event-settings-webexport',
                component: EventCourseSettingsWebExport,
                meta,
            },
            {
                path: 'privacy',
                name: 'event-settings-course-privacy',
                component: EventCourseSettingsPrivacy,
                meta,
            },
            {
                path: 'legal-notice',
                name: 'event-settings-course-legal-notice',
                component: EventCourseSettingsLegalNotice,
                meta,
            },
            {
                path: 'email',
                name: 'event-settings-course-email',
                component: EventCourseSettingsEmail,
                meta,
            },
            {
                path: 'email-template',
                name: 'event-settings-email-template',
                component: EventCourseSettingsEmailTemplate,
                meta,
            },
            {
                path: 'contact',
                name: 'event-settings-course-contact',
                component: EventCourseSettingsContact,
                meta,
            },
            {
                path: 'contact-form',
                name: 'event-settings-contact-form',
                component: EventCourseSettingsContactForm,
                props()
                {
                    return {
                        resource,
                    };
                },
                meta,
            },
            {
                path: 'editor',
                name: 'event-settings-editor',
                component: EventEditors,
                meta,
            },
            {
                path: 'categories',
                name: 'event-settings-categories',
                component: CategoryManagement,
                props()
                {
                    return {
                        breadcrumbs: [
                            {
                                label: 'Veranstaltungen',
                                href: {name: 'event-list'},
                            },
                        ],
                        route: '/event/categories',
                        tag: 'div',
                    };
                },
                meta,
            },
            {
                path: 'global',
                name: 'event-settings-global',
                component: EventSettingsGlobal,
                meta,
            },
        ],
    },
    {
        name: 'event-mail-template-edit',
        path: '/event/mail-template/edit/:type/:id',
        component: EventCourseMailTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                type: parseInt(route.params.type),
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'event-list-course',
        path: '/event/course',
        component: EventList,
        meta,
        props()
        {
            return {
                resource: 'event',
                type: 'tile',
                course: true,
            };
        },
    },

    /* edit */
    {
        name: 'event-course-edit',
        component: EventCourseEdit,
        path: '/event/course/edit/:id',
        props(route: RouteLocationStringParam)
        {
            return {
                resource: 'event',
                id: parseInt(route.params.id),
                course: true,
            };
        },
        meta,
    },
    {
        name: 'event-course-add',
        component: EventCourseEdit,
        path: '/event/course/add',
        props()
        {
            return {
                resource: 'event',
                course: true,
            };
        },
        meta,
    },
    {
        name: 'event-course-copy',
        component: EventCourseEdit,
        path: '/event/course/copy/:id',
        props(route: RouteLocationStringParam)
        {
            return {
                resource: 'event',
                id: parseInt(route.params.id),
                course: true,
            };
        },
        meta,
    },
    {
        name: 'event-course-view',
        component: EventCourseView,
        path: '/event/course/view/:id',
        props(route: RouteLocationStringParam)
        {
            return {
                resource: 'event',
                id: parseInt(route.params.id),
                course: true,
            };
        },
        meta,
    },
    {
        name: 'event-course-booking-list',
        path: '/event/course/booking-list',
        component: EventCourseBookingList,
        meta,
        props()
        {
            return {
                resource: 'event',
                type: 'tile',
                course: true,
            };
        },
    },
    {
        name: 'event-course-booking-view',
        path: '/event/course/booking-view/:id',
        component: EventCourseBookingView,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
        meta,
    },
    {
        name: 'event-course-booking-client',
        path: '/event/course/booking-client/:id/:token(\\w{32})?',
        component: EventCourseBookingClient,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
                token: route.params.token || '',
            };
        },
        meta,
    },
    {
        name: 'event-course-booking-confirm',
        path: '/event/course/booking-confirm/:id/:token(\\w{32}|\\w{40})',
        component: EventCourseBookingConfirm,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
                token: route.params.token,
            };
        },
        meta,
    },
    {
        name: 'event-course-booking-cancel',
        path: '/event/course/booking-cancel/:token(\\w{32}|\\w{40})',
        component: EventCourseBookingCancel,
        props(route: RouteLocationStringParam)
        {
            return {
                token: route.params.token,
            };
        },
        meta,
    },

    /* compatibility routes */
    {
        path: '/event/editor',
        redirect: {name: 'event-settings-editor'},
    },
    {
        path: '/event/event/list/mine/1',
        redirect: {name: 'event-list-mine'},
    },
    {
        path: '/event/entry/print/id/:id/start/:start/:wildcard*',
        redirect: {name: 'event-print'},
    },
    {
        path: '/event/calendar/seteditors',
        redirect: {name: 'event-settings-editor'},
    },
    {
        path: '/event/calendar/set-editors',
        redirect: {name: 'event-settings-editor'},
    },
    {
        path: '/event/entry/poll-list/range/:range',
        redirect: {name: 'event-poll-list'},
    },
    {
        name: 'event-booking',
        path: '/event-booking',
        redirect: {name: 'event-booking-list'},
        component: Booking,
        meta: metaBooking,
        children: [
            {
                name: 'event-booking-list',
                path: 'list',
                component: EventBookingList,
                meta: metaBooking,
            },
            {
                name: 'event-booking-view',
                path: 'view/:id',
                component: EventBookingViewTab,
                props(route: RouteLocationStringParam)
                {
                    return {
                        id: parseInt(route.params.id),
                    };
                },
                meta: metaBooking,
            },
            {
                name: 'event-booking-client',
                path: 'client/:id',
                component: EventBookingClient,
                props(route: RouteLocationStringParam)
                {
                    return {
                        id: parseInt(route.params.id),
                    };
                },
                meta: metaBooking,
            },
            {
                name: 'event-booking-confirm',
                path: 'confirm/:id/:token(\\w{32}|\\w{40})',
                component: EventBookingConfirm,
                props(route: RouteLocationStringParam)
                {
                    return {
                        id: parseInt(route.params.id),
                        token: route.params.token,
                    };
                },
                meta: metaBooking,
            },
            {
                name: 'event-booking-cancel',
                path: 'cancel/:id/:token(\\w{32}|\\w{40})',
                component: EventBookingCancel,
                props(route: RouteLocationStringParam)
                {
                    return {
                        id: parseInt(route.params.id),
                        token: route.params.token,
                    };
                },
                meta: metaBooking,
            },
        ],
    },
];

addCalendarRoutes('event', routes, {
        calendarList: EventList,
        entryEdit: EventEntryEdit,
        entryView: EventEntryView,
    },
    meta,
);

export default routes;